// @ts-check
// =============================================================================
// js/screens/lobby.js — the match lobby, the game's primary hub.
//
// WHAT THIS IS (plan/lobby.md; old RenderMainNew :776 + RenderLobbyPortion :855)
//   The default screen inside the nav shell: your Selected Deck, the
//   "Choose A New Deck" hero grid (opens the deck-list picker), and the live
//   Deckbiting Lobby list — host, join, load, cancel, spectate.
//
// LIVE FEED (do not poll)
//   store.lobby is fed by the socket's 'Lobby Update'; we subscribe and
//   repaint. Duels idle >10 minutes are hidden unless you're in them.
//
// TIER GATING
//   Guests: no Host/Duel-Bot (server rejects them anyway) — they join open
//   guest-invite games or spectate, with a join pitch instead of buttons.
//   Free users: hosting is capped by the server-wide 10-free-duel limit.
// =============================================================================

import { store, subscribe, setSlice } from '../core/store.js';
import { navigate } from '../core/router.js';
import { request } from '../net/http.js';
import { can } from '../app/session.js';
import { callStatsAPI } from '../app/analytics.js';
import { openDeckList, selectDeck, HERO_ASSET_NAMES } from '../ui/deck-list.js';
import { balanceRows } from '../ui/balanced-rows.js';
import { escapeHtml } from '../ui/modal.js';
import { showUpsell } from '../ui/upsell.js';
import { toast } from '../ui/toast.js';
import { requestGameFullscreen, exitGameFullscreen } from '../ui/fullscreen.js';
import { hideStaleDuel, lobbyRank } from '../ui/lobby-visibility.js';

/** @type {Array<() => void>} store unsubscribers while mounted */
let stops = [];

/** @type {HTMLElement|null} */
let screenEl = null;

/**
 * Render the lobby.
 * @param {HTMLElement} container
 * @param {Record<string,string>} _params
 * @returns {void}
 */
export function render(container, _params) {
	screenEl = document.createElement('div');
	screenEl.className = 'screen screen--lobby'; // column layout: the lobby stage grows to the floor
	container.appendChild(screenEl);

	// Default the selected deck like the old client: first prebuilt (:784).
	if (!store.loadedDeck?.list) {
		const fallback = store.userData?.defaultdecks?.[0];
		if (fallback?.list) {
			selectDeck(fallback, fallback.index);
		}
	}

	paint();
	stops = [
		subscribe('lobby', paint),
		subscribe('loadedDeck', paint),
		subscribe('userData', paint),
	];
	// Keep the hero rows balanced as the viewport changes (paint() also
	// balances after every repaint — this covers pure resizes).
	window.addEventListener('resize', onResize);
}

/** Debounced resize -> re-balance the hero grid rows. */
let resizeTimer = /** @type {ReturnType<typeof setTimeout>|null} */ (null);
function onResize() {
	if (resizeTimer) clearTimeout(resizeTimer);
	resizeTimer = setTimeout(() => balanceRows(screenEl?.querySelector('#heroes-grid')), 100);
}

/** Unsubscribe from the live feed. */
export function destroy() {
	stops.forEach((stop) => stop());
	stops = [];
	window.removeEventListener('resize', onResize);
	if (resizeTimer) clearTimeout(resizeTimer);
	resizeTimer = null;
	screenEl = null;
}

/** Repaint the whole hub from current state. */
function paint() {
	if (!screenEl) return;
	const { userData, loadedDeck } = store;
	if (!userData) return;

	let html = '';

	// --- Selected Deck panel (old :795) ------------------------------------
	if (loadedDeck?.list) {
		const heroName = HERO_ASSET_NAMES[loadedDeck.hero] || '???';
		html += `
			<span class="lobby-heading">Selected Deck</span>
			<div class="selected-deck-wrap">
				<div class="selected-deck-card" id="selected-deck-card" title="Your decks for ${escapeHtml(heroName)}" role="button" tabindex="0">
					<div class="selected-deck-art"><img src="/leaders/faces/${heroName}.png" alt="${escapeHtml(heroName)}"></div>
					<div class="selected-deck-caption">
						<span class="selected-deck-hero">${escapeHtml(heroName).toUpperCase()}</span>
						<span class="selected-deck-name">${escapeHtml(loadedDeck.nickname || heroName)}</span>
					</div>
				</div>
			</div>`;
	}

	// --- Hero grid: pick/build a deck per hero (old :811) -------------------
	const decks = Array.isArray(userData.decks) ? userData.decks : [];
	html += `<span class="lobby-heading">Choose A New Deck</span><div id="heroes-grid" class="heroes-grid">`;
	HERO_ASSET_NAMES.forEach((name, heroId) => {
		// Deck count = the prebuilt + your saved decks for that hero.
		const qty = 1 + decks.filter((d) => d && d.hero != null && String(d.hero) === String(heroId)).length;
		html += `
			<div class="hero-card" data-hero="${heroId}">
				<img src="/leaders/faces/${name}.png" alt="${name}">
				<div class="hero-caption">
					<span class="hero-caption-name">${name.toUpperCase()}</span>
					<span class="hero-caption-count">${qty} deck${qty === 1 ? '' : 's'}</span>
				</div>
			</div>`;
	});
	html += `</div>`;

	html += buildLobbyList();
	screenEl.innerHTML = html;
	wireEvents();
	// Even out the leader rows (fewest rows, evenly filled — see balanced-rows).
	balanceRows(screenEl.querySelector('#heroes-grid'));
}

/**
 * The Deckbiting Lobby section (old RenderLobbyPortion :855).
 * @returns {string} HTML
 */
function buildLobbyList() {
	const { userData, loadedDeck, duel } = store;
	const { duels } = store.lobby;
	const now = Date.now();

	// Hide stale games — 10+ min idle, STARTED, and not ours to play or
	// watch (ui/lobby-visibility.js has the full rule).
	let inMatch = false;
	const visible = [];
	for (const listed of duels) {
		const involved = listed.player1?.id === userData.id || listed.player2?.id === userData.id;
		if (involved) inMatch = true;
		if (hideStaleDuel(listed, userData.id, duel?.id, now)) continue;
		visible.push(listed);
	}
	// Group by usefulness — own games, open seats, live PvP, then bot games —
	// keeping the newest-first server order within each group (stable sort).
	visible.sort((a, b) => lobbyRank(a, userData.id) - lobbyRank(b, userData.id));

	// The lobby STAGE: a panel rising from the bottom of the screen (open
	// bottom edge — .lobby-stage grows to the viewport floor). Title, then
	// the match actions as its subheader, then the duel rows.
	let html = `<section class="lobby-stage">`;
	html += `<span class="lobby-heading lobby-heading--lobby">Deckbiting Lobby</span>`;

	// Top bar: host buttons for members, Duel Bot for everyone with a deck
	// (guests fight the bot with prebuilt decks — the server enforces that).
	html += `<div class="lobby-topbar">`;
	if (loadedDeck && !inMatch) {
		if (can('host')) {
			html += `<button class="defaultButton" id="btn-host">Host Game</button>
				<button class="defaultButton" id="btn-host-guest" title="Host a private game and invite a friend by link">Invite A Friend</button>
				<span id="hostGameMessage"></span>`;
		} else {
			// Guests can't host — but show the Host Game button greyed out
			// rather than a bare "join today" line. Tapping it explains how to
			// unlock hosting (become a free member / log in).
			html += `<button class="defaultButton is-locked" id="btn-host-locked" title="Log in to host duels against other players">Host Game</button>`;
		}
		if (can('duelBot')) {
			html += `<button class="defaultButton" id="btn-duel-bot">Duel Bot</button>`;
		}
	}
	if (!loadedDeck) {
		html += `<i>Select a deck to duel!</i>`;
	} else if (inMatch) {
		html += `<i>You're already in a match.</i>`;
	}
	html += `</div>`;

	// The live duel rows.
	html += `<div id="lobby-list" class="lobby-list">`;
	for (const listed of visible) {
		const p1Raw = listed.player1?.nickname || 'Unknown';
		const p2Raw = listed.player2?.nickname || 'Open Seat';
		const p1 = p1Raw === userData.nickname ? 'YOU' : p1Raw;
		const p2 = p2Raw === userData.nickname ? 'YOU' : p2Raw;
		const isYours = listed.player1?.id === userData.id || listed.player2?.id === userData.id;

		let actions = '';
		const isOpenSeat = loadedDeck && listed.player1.id !== userData.id && listed.player2.id === false;
		// Guests may take a seat only in a private game invited to their token;
		// members may take any open seat.
		const canTakeSeat = isOpenSeat && (!store.session.isGuest || listed.guest === userData.guest);
		if (canTakeSeat) {
			// An open seat we could take — if our deck fits the format.
			const formatOk =
				listed.format === 'Standard' ||
				(loadedDeck.index == 100 && listed.format === 'Beginner') ||
				(loadedDeck.index >= 100 && listed.format === 'Prebuilt');
			actions += formatOk
				? `<button class="defaultButton js-join" data-duel="${listed.id}">Duel ${escapeHtml(listed.player1.nickname)}</button>`
				: `<i> ${escapeHtml(listed.format)} decks only</i>`;
		} else if (isOpenSeat && store.session.isGuest && !listed.guest) {
			// A public open seat, but we're a guest — joining a real PvP duel
			// needs an account. Show the join button greyed; tapping it pops the
			// login/upsell (guest-invite games are handled by canTakeSeat above).
			actions += `<button class="defaultButton is-locked js-join-locked" data-duel="${listed.id}">Duel ${escapeHtml(listed.player1.nickname)}</button>`;
		} else if (isYours) {
			if ((!duel || duel.id !== listed.id) && listed.player2.id !== false) {
				actions += `<button class="defaultButton js-load" data-duel="${listed.id}">Load</button>`;
			}
			if (listed.player1.id === userData.id && listed.guest) {
				if (listed.player2.id === false) {
					actions += ` <button class="defaultButton js-kill" data-duel="${listed.id}">Cancel Duel</button>`;
				}
				actions += ` <span class="guest-link">Link: ${location.origin}/?guest=${escapeHtml(listed.guest)}</span>`;
			} else {
				actions += ' ← Your Game ';
				if (listed.player2.id === false) {
					actions += ` <button class="defaultButton js-kill" data-duel="${listed.id}">Cancel Duel</button>`;
				}
			}
		} else if (listed.player2.id) {
			actions += `<button class="defaultButton js-spectate" data-duel="${listed.id}">Spectate</button>`;
		}

		html += `
			<div class="duelCard${isYours ? ' duelCard--yours' : ''}">
				<div class="duelMeta">
					<div class="duelPlayers">
						<span class="duelP1">${escapeHtml(p1)}</span>
						<span class="duelVs">vs</span>
						<span class="duelP2">${escapeHtml(p2)}</span>
					</div>
					<div class="duelTags">
						<span class="duelTag">${escapeHtml(listed.format)}</span>
						<span class="duelTag">${escapeHtml(listed.mode)}</span>
						<span class="duelTag">${escapeHtml(listed.clock)}</span>
					</div>
				</div>
				<div class="duelActions">${actions}</div>
			</div>`;
	}
	if (!visible.length) {
		html += `<div class="empty-state">No Matches Found</div>`;
	}
	html += `</div></section>`;
	return html;
}

/** Wire up the freshly painted DOM. */
function wireEvents() {
	if (!screenEl) return;
	for (const card of screenEl.querySelectorAll('.hero-card')) {
		card.addEventListener('click', () => openDeckList(card.getAttribute('data-hero') || 0));
	}
	// The Selected Deck panel opens its own leader's picker (with the loaded
	// deck's row highlighted there).
	const selectedCard = screenEl.querySelector('#selected-deck-card');
	if (selectedCard && store.loadedDeck?.hero != null) {
		selectedCard.addEventListener('click', () => openDeckList(store.loadedDeck.hero));
		selectedCard.addEventListener('keydown', (e) => {
			if (/** @type {KeyboardEvent} */ (e).key === 'Enter') openDeckList(store.loadedDeck.hero);
		});
	}
	screenEl.querySelector('#btn-host')?.addEventListener('click', () => hostGame(false));
	screenEl.querySelector('#btn-host-guest')?.addEventListener('click', () => hostGame(true));
	screenEl.querySelector('#btn-duel-bot')?.addEventListener('click', () => navigate('challenge'));
	// Greyed guest Host/Join buttons: explain that hosting/joining PvP needs an
	// account, with a link to become a free member (or log in).
	screenEl.querySelector('#btn-host-locked')?.addEventListener('click', () => showUpsell('host'));
	for (const btn of screenEl.querySelectorAll('.js-join-locked')) {
		btn.addEventListener('click', () => showUpsell('host'));
	}
	for (const btn of screenEl.querySelectorAll('.js-join')) {
		btn.addEventListener('click', () => joinGame(btn.getAttribute('data-duel')));
	}
	for (const btn of screenEl.querySelectorAll('.js-load')) {
		btn.addEventListener('click', () => loadGame(btn.getAttribute('data-duel')));
	}
	for (const btn of screenEl.querySelectorAll('.js-kill')) {
		btn.addEventListener('click', () => killGame(btn.getAttribute('data-duel')));
	}
	for (const btn of screenEl.querySelectorAll('.js-spectate')) {
		btn.addEventListener('click', () => spectateGame(btn.getAttribute('data-duel')));
	}
}

// -----------------------------------------------------------------------------
// The lobby request functions (old HostGame :3333, JoinGame :3444, …).
// The socket's 'Lobby Update' refreshes the list; joins/loads set the duel
// (boot's duel subscription routes into #/duel).
//
// MOBILE FULLSCREEN: every button that sends the player into a game requests
// fullscreen SYNCHRONOUSLY, inside the click, before the awaited request —
// the gesture is only guaranteed valid while the handler is on the stack, so
// by the time the duel mounts, fullscreen is already up (ui/fullscreen.js).
// If the server then refuses, we exit again so a failed host/join doesn't
// strand the player fullscreen in the lobby. Hosting fullscreens too: the
// host is socket-pushed into the duel (gesturelessly) when an opponent joins,
// so the Host click is the only gesture we ever get from them.
// -----------------------------------------------------------------------------

/**
 * Host an open game — or a private guest-invite game.
 * @param {boolean} forGuest
 */
async function hostGame(forGuest) {
	const { userData, loadedDeck } = store;
	// The free-tier cap (server enforces too; this mirrors the old client UX).
	if (userData.freeUser && (store.lobby.deets.freeDuels || 0) >= 10) {
		const msg = screenEl?.querySelector('#hostGameMessage');
		if (msg) msg.textContent = ' Free Lobby Full';
		return;
	}
	requestGameFullscreen();
	const returned = await request('Host Game', {
		deckIndex: loadedDeck.index,
		guest: forGuest ? userData.guest : false,
	});
	if (returned?.deckInvalid) {
		exitGameFullscreen();
		toast(returned.reason || 'That deck is no longer valid — update it in the deck builder.', { error: true });
		return;
	}
	if (!returned || returned.message === 'Fail') {
		exitGameFullscreen();
		toast(returned?.reason || 'Could not host right now.', { error: true });
		return;
	}
	callStatsAPI('Duel:Host');
	setSlice('lobby', { ...store.lobby, duels: returned.duels || store.lobby.duels });
}

/**
 * Take the open seat in a hosted duel.
 * @param {string|null} duelId
 */
async function joinGame(duelId) {
	requestGameFullscreen();
	const returned = await request('Join Game', { duel: duelId, deckIndex: store.loadedDeck.index });
	if (returned?.deckInvalid) {
		exitGameFullscreen();
		toast(returned.reason || 'That deck is no longer valid — update it in the deck builder.', { error: true });
		return;
	}
	if (!returned || returned.message === 'Fail') {
		exitGameFullscreen();
		toast('Could not join that game.', { error: true });
		return;
	}
	callStatsAPI('Duel:Join');
	if (returned.duels) setSlice('lobby', { ...store.lobby, duels: returned.duels });
	if (returned.duel) setSlice('duel', returned.duel); // -> routes into #/duel
}

/**
 * Rejoin your live game.
 * @param {string|null} duelId
 */
async function loadGame(duelId) {
	requestGameFullscreen();
	const returned = await request('Load Game', { duel: duelId });
	if (!returned || returned.message === 'Fail') {
		exitGameFullscreen();
		toast('Could not load that game.', { error: true });
		return;
	}
	if (returned.duel) {
		setSlice('duel', returned.duel);
		// Explicit navigate: the auto-router only reacts to NEW duel ids, and
		// loading back into a game you stepped out of reuses the same id.
		navigate('duel');
	}
}

/**
 * Cancel your open-seat game.
 * @param {string|null} duelId
 */
async function killGame(duelId) {
	const returned = await request('Kill Game', { duel: duelId, deckIndex: store.loadedDeck?.index });
	if (!returned || returned.message === 'Fail') return;
	if (returned.duels) setSlice('lobby', { ...store.lobby, duels: returned.duels });
}

/**
 * Watch someone else's game (read-only duel).
 * @param {string|null} duelId
 */
async function spectateGame(duelId) {
	requestGameFullscreen();
	const returned = await request('Spectate Game', { duel: duelId });
	if (!returned || returned.message === 'Fail') {
		exitGameFullscreen();
		toast('Could not spectate that game.', { error: true });
		return;
	}
	if (returned.duels) setSlice('lobby', { ...store.lobby, duels: returned.duels });
	if (returned.duel) setSlice('duel', returned.duel);
}
